import { useState } from "react";
import { InsightsCards, type InsightCardData } from "@/components/core/Insights/InsightsCards";
import { useWallet } from "@/hooks/useWallet";
import { useDashboardData } from "@/hooks/useDashboardData";
import { useAttribution } from "@/hooks/useAttribution";
import { useStrategyAnalysis } from "@/hooks/useStrategyAnalysis";
import { useSubscription } from "@/hooks/useSubscription";
import { LeverageOverlayChart } from "@/components/charts/LeverageOverlayChart";
import { StrategySegmentationSection } from "@/components/insights/StrategySegmentationSection";
import { LeverageAnalysisSection } from "@/components/insights/LeverageAnalysisSection";
import { PerformanceAttributionSection } from "@/components/insights/PerformanceAttributionSection";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Lightbulb, TrendingUp, TrendingDown, ChevronDown, ChevronUp, AlertCircle, RefreshCw } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";

export default function PerpsInsights() {
  const { address } = useWallet();
  const { subscription, isPro } = useSubscription(address);
  const { data, isLoading, error, rangeDays } = useDashboardData({ address, rangeDays: 30, marketType: 'perps' });
  const { data: attributionData, isLoading: isAttributionLoading, error: attributionError } = useAttribution({
    address,
    rangeDays: 30,
    marketType: 'perps',
  });

  const { data: strategyData, isLoading: isStrategyLoading, error: strategyError } = useStrategyAnalysis({
    address,
    rangeDays: 30,
    marketType: 'perps',
  });

  const [showAllCoins, setShowAllCoins] = useState(false);

  const fillsCount = data?.kpis.fillsCount ?? 0;
  const totalFees = data?.kpis.totalFees ?? 0;
  const totalVolume = data?.kpis.totalVolume ?? 0;
  const feeBps = totalVolume > 0 ? (totalFees / totalVolume) * 10000 : null;
  const tradesPerDay = rangeDays > 0 ? fillsCount / rangeDays : 0;
  const maxDrawdown = data?.kpis.maxDrawdown;

  const severityInfo: InsightCardData["severity"] = "info";
  const severityWarning: InsightCardData["severity"] = "warning";

  // Basic insights from dashboard data
  const basicInsights = [
    {
      id: "leak-fees",
      title: "Fee drag",
      description: feeBps == null
        ? "Not enough volume to estimate fee drag."
        : feeBps > 8
          ? "Your fees are high relative to volume. Consider reducing taker usage / overtrading."
          : "Fee drag looks reasonable for this volume.",
      value: feeBps == null ? "N/A" : `${feeBps.toFixed(2)} bps`,
      severity: feeBps == null ? severityInfo : feeBps > 8 ? severityWarning : severityInfo,
    },
    {
      id: "behavior-activity",
      title: "Trade frequency",
      description: tradesPerDay > 50
        ? "High trade count per day. If performance is flat, consider cutting low-conviction churn."
        : "Trade frequency looks moderate.",
      value: `${tradesPerDay.toFixed(1)} trades/day`,
      severity: tradesPerDay > 50 ? severityWarning : severityInfo,
    },
    {
      id: "risk-dd",
      title: "Drawdown risk",
      description: maxDrawdown == null
        ? "Not enough portfolio history points to compute drawdown."
        : Math.abs(maxDrawdown) > 0.2
          ? "Large drawdown observed. Consider reducing leverage and sizing in adverse regimes."
          : "Drawdown is within a healthy range.",
      value: maxDrawdown == null ? "N/A" : `${(maxDrawdown * 100).toFixed(1)}%`,
      severity: maxDrawdown == null ? severityInfo : Math.abs(maxDrawdown) > 0.2 ? severityWarning : severityInfo,
    },
  ];

  const formatCurrency = (value: number | null | undefined) => {
    if (value == null) return "N/A";
    const sign = value >= 0 ? "+" : "";
    if (Math.abs(value) >= 1000) {
      return `${sign}$${(value / 1000).toFixed(1)}k`;
    }
    return `${sign}$${value.toFixed(2)}`;
  };

  const formatPercent = (value: number | null | undefined) => {
    if (value == null) return "N/A";
    return `${(value * 100).toFixed(1)}%`;
  };

  const displayedCoins = showAllCoins 
    ? attributionData?.coinBreakdown 
    : attributionData?.coinBreakdown?.slice(0, 5);

  // Consolidated error state
  const hasError = error || attributionError || strategyError;
  const errorMessages = [error, attributionError, strategyError].filter(Boolean);

  return (
    <main className="container px-6 py-8 max-w-[1600px]">
      {/* Page Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold">Perps Insights</h1>
        <p className="text-muted-foreground">Advanced analytics to improve your trading decisions</p>
      </div>

      {/* Error Alert */}
      {hasError && (
        <Alert variant="destructive" className="mb-6">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error loading data</AlertTitle>
          <AlertDescription>
            <ul className="list-disc list-inside space-y-1">
              {errorMessages.map((err, i) => (
                <li key={i}>{err}</li>
              ))}
            </ul>
            <p className="mt-2 text-xs">Try refreshing the page or syncing your wallet data again.</p>
          </AlertDescription>
        </Alert>
      )}

      {/* AI Insights from Attribution */}
      {attributionData?.insights && attributionData.insights.length > 0 && (
        <Card className="mb-6 border-primary/20 bg-primary/5">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <Lightbulb className="w-5 h-5 text-primary" />
              <CardTitle className="text-lg">AI-Generated Insights</CardTitle>
            </div>
            <CardDescription>Actionable observations from your trading data</CardDescription>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2">
              {attributionData.insights.map((insight, i) => (
                <li key={i} className="flex items-start gap-2 text-sm">
                  <span className="text-primary mt-0.5">•</span>
                  <span>{insight}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* Leverage Analysis Section - Free for all users */}
      <div className="mb-6">
        <LeverageAnalysisSection metrics={data?.leverage || null} isLoading={isLoading} />
      </div>

      {/* Leverage Overlay Chart - Free for all users */}
      {data?.leverageSeries && data.leverageSeries.length > 0 && (
        <div className="mb-6">
          <LeverageOverlayChart data={data.leverageSeries} loading={isLoading} />
        </div>
      )}

      {/* Performance Attribution (Pro feature with preview for free users) */}
      <div className="mb-6">
        <PerformanceAttributionSection
          attribution={attributionData?.attribution || null}
          isPro={isPro}
          isLoading={isAttributionLoading}
        />
      </div>

      {/* Per-Coin Attribution Table (Pro feature) */}
      {isPro && attributionData?.coinBreakdown && attributionData.coinBreakdown.length > 0 && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-base">Per-Coin Attribution</CardTitle>
            <CardDescription>Performance breakdown by trading pair</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Coin</TableHead>
                    <TableHead className="text-right">Total PnL</TableHead>
                    <TableHead className="text-right">Market Beta</TableHead>
                    <TableHead className="text-right">Timing Alpha</TableHead>
                    <TableHead className="text-right">Fees</TableHead>
                    <TableHead className="text-right">Trades</TableHead>
                    <TableHead className="text-right">Win Rate</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {displayedCoins?.map((coin) => (
                    <TableRow key={coin.coin}>
                      <TableCell className="font-medium">{coin.coin}</TableCell>
                      <TableCell className={cn(
                        "text-right font-mono",
                        coin.totalPnl >= 0 ? "text-emerald-500" : "text-rose-500"
                      )}>
                        <div className="flex items-center justify-end gap-1">
                          {coin.totalPnl >= 0 ? (
                            <TrendingUp className="w-3 h-3" />
                          ) : (
                            <TrendingDown className="w-3 h-3" />
                          )}
                          {formatCurrency(coin.totalPnl)}
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-mono text-muted-foreground">
                        {formatCurrency(coin.marketBeta)}
                      </TableCell>
                      <TableCell className={cn(
                        "text-right font-mono",
                        coin.timingAlpha >= 0 ? "text-emerald-500/80" : "text-rose-500/80"
                      )}>
                        {formatCurrency(coin.timingAlpha)}
                      </TableCell>
                      <TableCell className="text-right font-mono text-rose-500/60">
                        {formatCurrency(coin.fees)}
                      </TableCell>
                      <TableCell className="text-right text-muted-foreground">
                        {coin.tradeCount}
                      </TableCell>
                      <TableCell className="text-right">
                        <Badge variant={coin.winRate >= 0.5 ? "default" : "secondary"} className="font-mono">
                          {formatPercent(coin.winRate)}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            {attributionData.coinBreakdown.length > 5 && (
              <div className="mt-4 text-center">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowAllCoins(!showAllCoins)}
                  className="text-muted-foreground"
                >
                  {showAllCoins ? (
                    <>
                      <ChevronUp className="w-4 h-4 mr-1" />
                      Show Less
                    </>
                  ) : (
                    <>
                      <ChevronDown className="w-4 h-4 mr-1" />
                      Show All {attributionData.coinBreakdown.length} Coins
                    </>
                  )}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Strategy Segmentation (Pro feature with preview for free users) */}
      <div className="mb-6">
        <StrategySegmentationSection
          strategies={strategyData?.strategies || null}
          isPro={isPro}
          isLoading={isStrategyLoading}
        />
      </div>

      {/* Position Sizing Analysis (Pro feature) */}
      {isPro && attributionData?.positionSizing && attributionData.positionSizing.some(b => b.tradeCount > 0) && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-base">Position Sizing Analysis</CardTitle>
            <CardDescription>Performance by position size bucket</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Size Range</TableHead>
                    <TableHead className="text-right">Trades</TableHead>
                    <TableHead className="text-right">Win Rate</TableHead>
                    <TableHead className="text-right">Avg PnL/Trade</TableHead>
                    <TableHead className="text-right">Total PnL</TableHead>
                    <TableHead className="text-right">Max Loss</TableHead>
                    <TableHead className="text-right">Max Gain</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {attributionData.positionSizing
                    .filter(bucket => bucket.tradeCount > 0)
                    .map((bucket) => (
                      <TableRow key={bucket.sizeBucket}>
                        <TableCell className="font-medium">{bucket.sizeBucket}</TableCell>
                        <TableCell className="text-right text-muted-foreground">
                          {bucket.tradeCount}
                        </TableCell>
                        <TableCell className="text-right">
                          <Badge 
                            variant={bucket.winRate && bucket.winRate >= 0.5 ? "default" : "secondary"} 
                            className="font-mono"
                          >
                            {bucket.winRate != null ? `${(bucket.winRate * 100).toFixed(1)}%` : "N/A"}
                          </Badge>
                        </TableCell>
                        <TableCell className={cn(
                          "text-right font-mono",
                          bucket.avgPnlPerTrade >= 0 ? "text-emerald-500" : "text-rose-500"
                        )}>
                          {bucket.avgPnlPerTrade >= 0 ? "+" : ""}${bucket.avgPnlPerTrade.toFixed(2)}
                        </TableCell>
                        <TableCell className={cn(
                          "text-right font-mono",
                          bucket.totalPnl >= 0 ? "text-emerald-500" : "text-rose-500"
                        )}>
                          {formatCurrency(bucket.totalPnl)}
                        </TableCell>
                        <TableCell className="text-right font-mono text-rose-500/70">
                          {formatCurrency(bucket.maxLoss)}
                        </TableCell>
                        <TableCell className="text-right font-mono text-emerald-500/70">
                          {formatCurrency(bucket.maxGain)}
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Basic Insights Cards */}
      <InsightsCards
        title="Execution Diagnostics"
        subtitle="Perps-specific leaks, regime, and execution metrics"
        items={isLoading ? [] : basicInsights}
      />
    </main>
  );
}
